import { SIDEBAR_TAGS } from "~/core/slices/help/slice";
import { renderTemplate } from "./utils";

const applyTemplates = (val: string) =>
    val.replace(/{{(.*?)}}/g, (_, key: string) => {
        const [name, ...params] = key.split("|");

        const paramsObj = params.reduce(
            (acc, str) => {
                const delIdx = str.indexOf("=");
                const key = str.slice(0, delIdx).trim();
                const value = str.slice(delIdx + 1).trim();

                acc[key] = value;
                return acc;
            },
            {} as Record<string, string>,
        );

        const template = renderTemplate(name, paramsObj);

        if (!template) {
            return "";
        }

        return template;
    });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const applyContext = (val: string, ctx: Record<string, any>) =>
    val.replace(/\$(\w+?)/g, (_, key) => ctx[key.trim()]?.toString() ?? "");

// supports: h1-h6, ul, ol, li, p
//
const parseMarkdown = (val: string) => {
    const lines = val.split("\n");
    const result: string[] = [];
    const listTypes: string[] = [];

    const onNewList = (type: string) => {
        const lastListType = listTypes.at(-1);

        if (lastListType === type) {
            return;
        }

        const penultimate = listTypes.at(-2);

        if (penultimate === type) {
            result.push(`</${lastListType}>`);
            listTypes.pop();
        } else {
            result.push(`<${type}>`);
            listTypes.push(type);
        }
    };

    for (const line of lines) {
        if (line.startsWith("#")) {
            const level = line.match(/^#+/)?.[0].length ?? 0;
            const content = line.slice(level).trim();

            result.push(`<h${level}>${content}</h${level}>`);
        } else if (line.startsWith("-")) {
            onNewList("ul");

            result.push(`<li>${line.slice(1).trim()}</li>`);
        } else if (line.match(/^\d+\./)) {
            onNewList("ol");

            result.push(`<li>${line.slice(2).trim()}</li>`);
        } else {
            const lastListType = listTypes.pop();

            if (lastListType) {
                result.push(`</${lastListType}>`);
            }

            if (line.match(/<.*>/)) {
                result.push(line);
            } else {
                result.push(`<p>${line}</p>`);
            }
        }
    }

    listTypes.forEach((t) => result.push(`</${t}>`));

    return result.join("");
};

const getGoto = (tag: string, ctx?: Record<string, unknown>) => {
    const currentPath = String(ctx?.$path ?? "");
    const [path, subpath] = tag.split(".");
    let targetPath = path.replaceAll("-", "/");

    if (path === "header") {
        // it's a tag in the main layout, which means it's present on every page
        return "";
    }

    if (targetPath === "admin/time") {
        const calendar = ["month", "week", "day"].includes(subpath)
            ? subpath
            : "month";

        targetPath += `?calendar=${calendar}`;
    }

    return currentPath === targetPath ? "" : targetPath;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const preprocess = (val: string, ctx?: Record<string, any>) => {
    const SIDEBAR_PATH = "admin/time?calendar=month";

    const makeTooltip = (
        _: string,
        text: string,
        tooltip: string,
        link: string,
    ) => {
        const params = ["tooltip", `content=${text}`];
        const actions: string[] = [];
        const tag = link.includes(".") ? link.trim() : "";
        const goto = tag ? getGoto(tag, ctx) : link.trim();
        const isSidebarPath =
            goto === SIDEBAR_PATH || (!goto && ctx?.$path === SIDEBAR_PATH);

        if (goto) {
            actions.push(`goto(page=${goto})`);
        }

        if (
            isSidebarPath &&
            SIDEBAR_TAGS.includes(tag as (typeof SIDEBAR_TAGS)[number])
        ) {
            actions.push("admin_time_open_sidebar");
        }

        if (tag) {
            actions.push(`open_discovery(tag=${tag})`);
        }

        if (tooltip) {
            params.push(`tooltip-content=${tooltip}`);
        }

        if (actions.length) {
            params.push(`action=${actions.join(";")}`);
        }

        return `{{${params.join("|")}}}`;
    };

    return val.replaceAll(/\[(.*?)(?:\|(.*?))?\]\((.*?)\)/g, makeTooltip);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const build = (details: string, ctx?: Record<string, any>) => {
    let result = details;

    if (ctx) {
        result = applyContext(details, ctx);
    }

    return parseMarkdown(applyTemplates(preprocess(result, ctx)));
};
